import { config } from '@config/config';
import {
  getDataLayerSnippet,
  getGTMScript,
  getIframeSnippet,
} from '@config/gtm';

interface GTMEnvironment {
  gtm_auth: string;
  gtm_preview: string;
}

export interface GTMParams {
  dataLayer?: Record<string, unknown>;
  dataLayerName?: string;
  environment?: GTMEnvironment;
  nonce?: string;
  id?: string;
  customDomain?: string;
  customScriptName?: string;
}

const setupGTM = (params: GTMParams) => {
  const getDataLayerScript = () => {
    const dataLayerScript = document.createElement('script');
    if (params.nonce) {
      dataLayerScript.setAttribute('nonce', params.nonce);
    }
    dataLayerScript.innerHTML = getDataLayerSnippet({
      dataLayer: params.dataLayer,
      dataLayerName: params.dataLayerName,
    });
    return dataLayerScript;
  };

  const getNoScript = () => {
    const noScript = document.createElement('noscript');
    noScript.innerHTML = getIframeSnippet({
      id: params.id as string,
      environment: params.environment,
      customDomain: params.customDomain,
    });
    return noScript;
  };

  const getScript = () => {
    const script = document.createElement('script');
    if (params.nonce) {
      script.setAttribute('nonce', params.nonce);
    }
    script.innerHTML = getGTMScript({
      dataLayerName: params.dataLayerName as string,
      id: params.id as string,
      environment: params.environment,
      customDomain: params.customDomain,
    });
    return script;
  };

  return { getDataLayerScript, getNoScript, getScript };
};

export const initGTM = ({
  id = config.gtm.id,
  dataLayerName = config.gtm.dataLayerName,
  dataLayer,
  environment,
  nonce,
  customDomain,
  customScriptName,
}: GTMParams) => {
  const gtm = setupGTM({
    dataLayer,
    dataLayerName,
    environment,
    nonce,
    id,
    customDomain,
    customScriptName,
  });

  const dataLayerScript = gtm.getDataLayerScript();
  const script = gtm.getScript();
  const noScript = gtm.getNoScript();

  // dataLayer init must be inserted before the gtm.js loader, otherwise the
  // queue does not exist yet when gtm.js runs.
  document.head.insertBefore(dataLayerScript, document.head.childNodes[0]);
  document.head.insertBefore(script, document.head.childNodes[1]);
  document.body.insertBefore(noScript, document.body.childNodes[0]);
};

export const sendDataToGTM = (data: Record<string, unknown>): void => {
  const { dataLayerName } = config.gtm;
  const dataLayer = (window as unknown as Record<string, unknown[]>)[
    dataLayerName
  ];
  if (dataLayer) {
    dataLayer.push(data);
  } else {
    console.warn(
      `dataLayer ${dataLayerName} does not exist, needs script to be initialized`,
    );
  }
};
